import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Heart, MessageCircle, Bookmark, Share2, MoreHorizontal, Edit, Trash2, ImageIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePosts } from '../../contexts/PostContext';
import { Post } from '../../contexts/PostContext';
import FactRating from './FactRating';
import EditPostModal from './EditPostModal';

interface PostCardProps {
  post: Post;
}

const PostCard: React.FC<PostCardProps> = ({ post }) => {
  const { currentUser } = useAuth();
  const { likePost, savePost, deletePost } = usePosts();
  const [showMenu, setShowMenu] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  const isCurrentUser = currentUser?.id === post.userId;

  const handleLike = async () => {
    if (!currentUser) return;
    try {
      await likePost(post.id);
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const handleSave = async () => {
    if (!currentUser) return;
    try {
      await savePost(post.id);
    } catch (error) {
      console.error('Error saving post:', error);
    }
  };

  const handleShare = async () => {
    const postUrl = `${window.location.origin}/home/posts/${post.id}`;
    const shareData = {
      title: post.subject,
      text: `Check out this post: ${post.subject}`,
      url: postUrl
    };

    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(postUrl);
        alert('Link copied to clipboard!');
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('Error sharing:', error);
        try {
          await navigator.clipboard.writeText(postUrl);
          alert('Link copied to clipboard!');
        } catch (clipboardError) {
          console.error('Error copying to clipboard:', clipboardError);
        }
      }
    }
  };

  const handleDelete = async () => {
    if (!currentUser || !isCurrentUser) return;
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await deletePost(post.id);
      } catch (error) {
        console.error('Error deleting post:', error);
      }
    }
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    console.log(`Failed to load image for post: ${post.id} URL: ${post.photo}`);
    setImageError(true);
    setImageLoading(false);
  };

  return (
    <article className="bg-[#272727] rounded-xl overflow-hidden">
      {/* Post Header */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Link to={`/home/profile/${post.username.toLowerCase()}`}>
            <img 
              src={post.userPhotoURL} 
              alt={post.username}
              className="w-10 h-10 rounded-full object-cover border border-gray-700"
            />
          </Link>
          <div>
            <Link 
              to={`/home/profile/${post.username.toLowerCase()}`}
              className="font-medium text-white hover:text-purple-400 transition-colors"
            >
              {post.username}
            </Link>
            <p className="text-sm text-gray-400">
              {formatDistanceToNow(new Date(post.createdAt), { addSuffix: true })}
            </p>
          </div>
        </div>

        {isCurrentUser && (
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 text-gray-400 hover:text-white transition-colors"
            >
              <MoreHorizontal className="h-5 w-5" />
            </button>
            
            {showMenu && (
              <div className="absolute right-0 top-full mt-1 bg-[#272727] rounded-lg shadow-lg border border-gray-700 py-1 z-10">
                <button
                  onClick={() => {
                    setShowEditModal(true);
                    setShowMenu(false);
                  }}
                  className="flex items-center px-4 py-2 text-sm text-white hover:bg-[#404040] w-full text-left"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </button>
                <button
                  onClick={() => {
                    handleDelete();
                    setShowMenu(false);
                  }}
                  className="flex items-center px-4 py-2 text-sm text-red-400 hover:bg-[#404040] w-full text-left"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Post Image */}
      <div className="relative aspect-video bg-gray-800">
        {imageError ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
            <div className="text-center text-gray-400">
              <ImageIcon className="h-12 w-12 mx-auto mb-2" />
              <p className="text-sm">Image unavailable</p>
            </div>
          </div>
        ) : (
          <>
            {imageLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
              </div>
            )}
            <img 
              src={post.photo}
              alt={post.subject}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-200 ${
                imageLoading ? 'opacity-0' : 'opacity-100'
              }`}
              onLoad={handleImageLoad}
              onError={handleImageError}
              loading="lazy"
            />
          </>
        )}
      </div>

      {/* Post Content */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <Link 
            to={`/home/posts/${post.id}`}
            className="text-xl font-semibold text-white hover:text-purple-400 transition-colors"
          >
            {post.subject}
          </Link>
          {post.topic && (
            <Link 
              to={`/home/topic/${post.topic.toLowerCase()}`}
              className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
            >
              #{post.topic}
            </Link>
          )}
        </div>

        <p className="text-gray-300 mb-4 line-clamp-3">
          {post.content}
        </p>

        {/* Post Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleLike}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                post.isLiked 
                  ? 'text-red-500 bg-red-500/10' 
                  : 'text-gray-400 hover:text-red-500 hover:bg-red-500/10'
              }`}
            >
              <Heart className={`h-5 w-5 ${post.isLiked ? 'fill-current' : ''}`} />
              <span>{post.likeCount}</span>
            </button>

            <Link
              to={`/home/posts/${post.id}`}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg text-gray-400 hover:text-blue-500 hover:bg-blue-500/10 transition-colors"
            >
              <MessageCircle className="h-5 w-5" />
              <span>{post.commentCount}</span>
            </Link>

            <button
              onClick={handleSave}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                post.isSaved 
                  ? 'text-yellow-500 bg-yellow-500/10' 
                  : 'text-gray-400 hover:text-yellow-500 hover:bg-yellow-500/10'
              }`}
            >
              <Bookmark className={`h-5 w-5 ${post.isSaved ? 'fill-current' : ''}`} />
            </button>

            <button
              onClick={handleShare}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg text-gray-400 hover:text-green-500 hover:bg-green-500/10 transition-colors"
            >
              <Share2 className="h-5 w-5" />
            </button>
          </div>

          <FactRating rating={post.factRating} postId={post.id} />
        </div>
      </div>

      {showEditModal && (
        <EditPostModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          post={post}
        />
      )}
    </article>
  );
};

export default PostCard;